import { ReactNode, useState } from "react";
import clsx from "clsx";
import { ArrowPathIcon, ClipboardDocumentIcon, PencilSquareIcon, SparklesIcon } from "@heroicons/react/24/outline";

import { Button } from "../base/Button";
import LoadingWheel from "../shared/LoadingWheel";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import useNotification from "../shared/Notifications/useNotifications";
import useCoreDeploymentOverview from "../../data/CoreDeployment/useCoreDeploymentOverview";
import useCoreDeploymentMutations from "../../data/CoreDeployment/useCoreDeploymentMutations";
import { ICoreDeploymentOverview } from "../../models/CoreDeployment/CoreDeploymentRun";
import { formatDateTime } from "./coreDeploymentMeta";

type Tab = "customer" | "internal";

function Notice({ tone, children }: { tone: "red" | "amber" | "gray"; children: ReactNode }) {
  return (
    <div
      className={clsx(
        "rounded-lg border p-3 text-sm whitespace-pre-line",
        tone === "red" && "border-red-200 bg-red-50 text-red-800",
        tone === "amber" && "border-amber-200 bg-amber-50 text-amber-800",
        tone === "gray" && "border-gray-200 bg-gray-50 text-gray-700",
      )}
    >
      {children}
    </div>
  );
}

function generatedLine(overview: ICoreDeploymentOverview): string {
  const by = overview.generatedByEmail ? `by ${overview.generatedByEmail}` : "automatically";
  const commits = `${overview.commitCount} commit${overview.commitCount === 1 ? "" : "s"}`;
  return `Written ${by} on ${formatDateTime(overview.generatedAt)} from ${commits}${overview.model ? ` · ${overview.model}` : ""}`;
}

/** The AI overview of what a finished run shipped: a customer version to copy (and edit) and an internal one. */
export function CoreDeploymentOverview({ runId }: { runId: string }) {
  const { overview } = useCoreDeploymentOverview(runId);
  const { generateOverview, saveCustomerText } = useCoreDeploymentMutations();
  const { addNotification } = useNotification();

  const [tab, setTab] = useState<Tab>("customer");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const data = overview.data;
  const customerText = data?.customerTextEdited ?? data?.customerText ?? "";
  const hasTexts = data?.status === "Generated";
  const configured = !!data && data.isAiConfigured && data.isGitHubConfigured;

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      addNotification({ variant: "success", primaryText: "Copied to clipboard" });
    } catch {
      addNotification({ variant: "error", primaryText: "Could not copy to the clipboard" });
    }
  };

  const handleGenerate = () => {
    generateOverview.mutate(runId, {
      onSuccess: () => {
        addNotification({ variant: "success", primaryText: "Overview written" });
        setConfirmOpen(false);
        setEditing(false);
      },
      onError: (error: Error) => {
        addNotification({ variant: "error", primaryText: error?.message || "Failed to write the overview" });
        setConfirmOpen(false);
      },
    });
  };

  const handleSave = () => {
    saveCustomerText.mutate(
      { runId, text: draft },
      {
        onSuccess: () => {
          addNotification({ variant: "success", primaryText: "Customer text saved" });
          setEditing(false);
        },
        onError: (error: Error) => {
          addNotification({ variant: "error", primaryText: error?.message || "Failed to save the customer text" });
        },
      },
    );
  };

  const startEditing = () => {
    setDraft(customerText);
    setEditing(true);
  };

  const renderBody = () => {
    if (overview.isLoading || !data) {
      return (
        <div className="py-6">
          <LoadingWheel />
        </div>
      );
    }

    if (generateOverview.isPending) {
      return <Notice tone="gray">Reading the deployed commits and writing the overview. This can take a minute or two.</Notice>;
    }

    if (!configured && !hasTexts) {
      return (
        <Notice tone="amber">
          {!data.isAiConfigured && "Anthropic:ApiKey is not set on this environment, so overviews cannot be written.\n"}
          {!data.isGitHubConfigured && "GitHub:Token is not set on this environment, so the deployed commits cannot be read."}
        </Notice>
      );
    }

    switch (data.status) {
      case null:
        return <Notice tone="gray">No overview yet. Finished deployments get one automatically within a few minutes, or write one now.</Notice>;
      case "Pending":
        return (
          <Notice tone="gray">
            Waiting for the worker to write the overview.
            {data.error ? `\nLast attempt: ${data.error}` : ""}
          </Notice>
        );
      case "Failed":
        return <Notice tone="red">{data.error || "The overview could not be written."}</Notice>;
      case "NoChanges":
        return <Notice tone="gray">The deployed pull requests carried no commits worth describing.</Notice>;
    }

    const text = tab === "customer" ? customerText : data.internalText ?? "";

    return (
      <div className="space-y-3">
        {data.error && <Notice tone="red">The last regenerate failed, so this is the previous version: {data.error}</Notice>}
        {data.commitsTruncated && (
          <Notice tone="amber">This deploy had more commits than GitHub lists, so the oldest ones were left out.</Notice>
        )}

        <div className="flex items-center gap-4 border-b border-gray-200">
          {(["customer", "internal"] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setTab(t);
                setEditing(false);
              }}
              className={clsx(
                "-mb-px border-b-2 px-1 pb-2 text-sm font-medium",
                tab === t ? "border-orange-600 text-orange-700" : "border-transparent text-gray-500 hover:text-gray-700",
              )}
            >
              {t === "customer" ? "For customers" : "Internal"}
            </button>
          ))}
          <span className="flex-1" />
          {!editing && text && (
            <button
              type="button"
              className="inline-flex items-center gap-1 pb-2 text-sm text-gray-600 hover:text-gray-900"
              onClick={() => handleCopy(text)}
            >
              <ClipboardDocumentIcon className="w-4 h-4" />
              Copy
            </button>
          )}
          {tab === "customer" && !editing && data.hasCustomerFacingChanges && (
            <button
              type="button"
              className="inline-flex items-center gap-1 pb-2 text-sm text-gray-600 hover:text-gray-900"
              onClick={startEditing}
            >
              <PencilSquareIcon className="w-4 h-4" />
              Edit
            </button>
          )}
        </div>

        {tab === "customer" && !data.hasCustomerFacingChanges ? (
          <Notice tone="gray">Nothing in this deploy is customer-facing, so there is nothing to send. See the internal version for what changed.</Notice>
        ) : editing ? (
          <div className="space-y-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={14}
              className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-orange-500 focus:ring-orange-500"
            />
            <p className="text-xs text-gray-500">Save an empty box to go back to the generated text.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outlined" size="sm" onClick={() => setEditing(false)} disabled={saveCustomerText.isPending}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saveCustomerText.isPending}>
                {saveCustomerText.isPending ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-900 whitespace-pre-wrap">{text}</div>
        )}

        <p className="text-xs text-gray-500">
          {generatedLine(data)}
          {tab === "customer" && data.customerTextEdited && data.editedAt && (
            <> · Edited by {data.editedByEmail ?? "-"} on {formatDateTime(data.editedAt)}</>
          )}
        </p>
      </div>
    );
  };

  const canGenerate = configured && !generateOverview.isPending && data?.status !== "Pending";
  const hasAny = !!data?.status;

  return (
    <div>
      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title={hasAny ? "Regenerate overview?" : "Write overview?"}
        message={
          "Reads the deployed commits from GitHub and asks Claude to describe them, which uses AI credit." +
          (data?.customerTextEdited ? "\nYour edited customer text is kept." : "")
        }
        confirmButtonText={hasAny ? "Regenerate" : "Write overview"}
        onConfirm={handleGenerate}
        isLoading={generateOverview.isPending}
      />

      <div className="flex items-center gap-2 mb-2">
        <SparklesIcon className="w-4 h-4 text-orange-600" />
        <h3 className="text-sm font-medium text-gray-900">AI overview</h3>
        <span className="flex-1" />
        {canGenerate && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm text-orange-700 hover:underline"
            onClick={() => setConfirmOpen(true)}
          >
            <ArrowPathIcon className="w-4 h-4" />
            {hasAny ? "Regenerate" : "Write now"}
          </button>
        )}
      </div>

      {renderBody()}
    </div>
  );
}
